/**
 * CotizarAvisos — Solicitud de cotización armada desde los avisos de material (13-oct).
 *
 * Pedido de Maxi viendo producción: el botón "+ Cotizar" de la alerta abría el formulario
 * de OC en blanco, "como para llenar todo desde el principio". Acá los ítems ya vienen de
 * los avisos (material, pieza/SKU, OT, cantidad faltante) y solo se eligen los proveedores.
 *
 * Se genera UNA solicitud por proveedor con todos los ítems (POST
 * /purchaseOrder/cotizar-desde-avisos). Con varios proveedores comparten rfqGroup y el
 * comparador de Compras las muestra juntas. ACEROS BRAVO LTDA. viene marcado de entrada
 * porque es el proveedor habitual de material; se puede desmarcar.
 *
 * Hoja inferior en el celular y diálogo centrado en escritorio (Hoja de Mi tablero).
 * Sin colores literales: todo pasa por var(--tab-*) (CI de colores).
 */
import React, { useEffect, useMemo, useState } from "react";
import backend from "../../backend";
import { C, boton, campo, Hoja } from "../tablero/componentes";

// Mismo texto que pone el backend (services/avisosCompra.js) cuando no se sabe el material.
const MATERIAL_POR_DEFINIR = "Material por definir";
const PROVEEDOR_PREFERIDO = /aceros\s*bravo/i;
const UNIDADES = ["un", "kg", "m", "trozo", "kilo", "litro", "caja", "rollo"];

// Material conocido del aviso: el que guardó el aviso o, si quedó "por definir" (avisos
// creados antes de cargar la ficha, p. ej. OT 8438), el de la ficha de la pieza
// (GET /avisosCompra trae `pieza.material`). Vacío si no se sabe en ninguno de los dos.
export const materialDeAviso = a => {
  const guardado = String(a?.materialNombre || "").trim();
  if (guardado && guardado !== MATERIAL_POR_DEFINIR) return guardado;
  return String(a?.pieza?.material || "").trim();
};

const referenciaDe = a => [
  [a.piezaNombre, a.piezaSku ? `(${a.piezaSku})` : ""].filter(Boolean).join(" "),
  a.workOrder?.number ? `OT ${a.workOrder.number}` : "",
].filter(Boolean).join(" · ");

const lineaDesdeAviso = a => {
  const material = materialDeAviso(a);
  return {
    avisoId: a._id,
    porDefinir: !material,
    material,
    referencia: referenciaDe(a),
    cantidad: String(a.faltante ?? ""),
    // Sin material de catálogo detrás, el aviso trae "un" por defecto; lo que se compra
    // a los proveedores de acero son trozos de barra (como las N°14 y N°15).
    unidad: a.material ? (a.unidad || "un") : "trozo",
  };
};

// Lo que llega al proveedor: SOLO el material con sus medidas. La pieza, el SKU (código
// del cliente) y la OT no se le muestran; quedan en las notas internas y en la OT de cada
// ítem (los pone el backend). Misma política que from-piece-alerts.
const descripcionDe = l => l.material.trim();

const etiqueta = { fontSize: 12, fontWeight: 700, color: C.suave, textTransform: "uppercase", letterSpacing: ".04em", margin: "14px 0 6px" };

const CotizarAvisos = ({ avisos, centrada = false, onCerrar, onListo, onRecargar }) => {
  const [lineas, setLineas] = useState(() => avisos.map(lineaDesdeAviso));
  const [entidades, setEntidades] = useState(null); // null = cargando
  const [elegidos, setElegidos] = useState([]);     // ids, en el orden en que se marcaron
  const [busqueda, setBusqueda] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let vivo = true;
    backend.get("entity")
      .then(r => {
        if (!vivo) return;
        const provs = (r.data || [])
          .filter(e => e.type === "provider" || e.type === "both")
          .sort((x, y) => String(x.name || "").localeCompare(String(y.name || ""), "es"));
        setEntidades(provs);
        const bravo = provs.find(e => PROVEEDOR_PREFERIDO.test(e.name || ""));
        if (bravo) setElegidos(prev => (prev.length ? prev : [bravo._id]));
      })
      .catch(e => { if (vivo) { setEntidades([]); setError("No se pudo cargar la lista de proveedores: " + (e?.response?.data?.error || e.message)); } });
    return () => { vivo = false; };
  }, []);

  const porId = useMemo(() => new Map((entidades || []).map(e => [e._id, e])), [entidades]);
  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const lista = entidades || [];
    const filtrada = q
      ? lista.filter(e => (e.name || "").toLowerCase().includes(q) || (e.rut || "").toLowerCase().includes(q))
      : lista.filter(e => PROVEEDOR_PREFERIDO.test(e.name || "") || elegidos.includes(e._id));
    return filtrada.slice(0, 30);
  }, [entidades, busqueda, elegidos]);

  const cambiarLinea = (i, campoLinea, valor) =>
    setLineas(prev => prev.map((l, j) => (j === i ? { ...l, [campoLinea]: valor } : l)));
  const alternar = id => setElegidos(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const faltaMaterial = lineas.some(l => !l.material.trim());
  const cantidadMala = lineas.some(l => !(Number(l.cantidad) > 0));
  const nSol = elegidos.length;
  const nItems = lineas.length;
  const puede = !enviando && nSol > 0 && nItems > 0 && !faltaMaterial && !cantidadMala;

  const generar = async () => {
    if (!puede) return;
    setEnviando(true);
    setError("");
    try {
      const { data } = await backend.post("purchaseOrder/cotizar-desde-avisos", {
        avisos: lineas.map(l => l.avisoId),
        proveedores: elegidos,
        items: lineas.map(l => ({ avisoId: l.avisoId, descripcion: descripcionDe(l), cantidad: Number(l.cantidad), unidad: l.unidad })),
      });
      onListo(data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setEnviando(false);
      // 409: otra persona tomó alguno de estos avisos; se refresca la lista de atrás.
      if (e?.response?.status === 409 && onRecargar) onRecargar();
    }
  };

  return (
    <Hoja onCerrar={onCerrar} cerrarConVelo={false} centrada={centrada} maxWidth={680}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontSize: 17, fontWeight: 700 }}>Solicitud de cotización</div>
        <button type="button" onClick={onCerrar} disabled={enviando} aria-label="Cerrar" style={{ ...boton(), minHeight: 40, padding: "4px 12px" }}>✕</button>
      </div>
      <div style={{ fontSize: 13, color: C.suave, marginTop: 4 }}>
        Materiales tomados de {avisos.length === 1 ? "la alerta" : `${avisos.length} alertas`}. Revise la cantidad y elija a quién pedir precio.
      </div>

      {/* (a) Ítems */}
      <div style={etiqueta}>Ítems ({nItems})</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {lineas.map((l, i) => (
          <div key={l.avisoId} style={{ border: `1px solid ${C.borde}`, borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 12, color: C.suave, marginBottom: 6, overflowWrap: "anywhere" }}>
              {l.referencia || "Sin pieza asociada"} <span style={{ fontStyle: "italic" }}>(referencia interna, no va al proveedor)</span>
            </div>
            <input
              value={l.material}
              onChange={e => cambiarLinea(i, "material", e.target.value)}
              placeholder={l.porDefinir ? `${MATERIAL_POR_DEFINIR}: escriba el material y sus medidas (obligatorio)` : "Material y medidas"}
              aria-label="Material"
              style={{ ...campo, borderColor: !l.material.trim() ? C.ambar : C.borde }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <input
                type="number" inputMode="decimal" min="0" step="any"
                value={l.cantidad}
                onChange={e => cambiarLinea(i, "cantidad", e.target.value)}
                aria-label="Cantidad"
                style={{ ...campo, flex: 1, borderColor: Number(l.cantidad) > 0 ? C.borde : C.rojo }}
              />
              <select value={l.unidad} onChange={e => cambiarLinea(i, "unidad", e.target.value)} aria-label="Unidad" style={{ ...campo, width: 110 }}>
                {[...new Set([l.unidad, ...UNIDADES])].map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>

      {/* (b) Proveedores */}
      <div style={etiqueta}>Proveedores</div>
      {elegidos.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {elegidos.map(id => (
            <button key={id} type="button" onClick={() => alternar(id)} title="Quitar"
              style={{ ...boton(C.azul, true), minHeight: 36, padding: "4px 10px", fontSize: 13 }}>
              {porId.get(id)?.name || "Proveedor"} ✕
            </button>
          ))}
        </div>
      )}
      <input
        value={busqueda}
        onChange={e => setBusqueda(e.target.value)}
        placeholder="Buscar proveedor por nombre o RUT para agregarlo…"
        aria-label="Buscar proveedor"
        style={campo}
      />
      <div style={{ display: "flex", flexDirection: "column", marginTop: 6, maxHeight: 220, overflowY: "auto" }}>
        {entidades === null && <div style={{ fontSize: 13, color: C.suave, padding: 8 }}>Cargando proveedores…</div>}
        {entidades !== null && visibles.length === 0 && (
          <div style={{ fontSize: 13, color: C.suave, padding: 8 }}>
            {busqueda.trim() ? "Ningún proveedor coincide." : "Escriba para buscar proveedores."}
          </div>
        )}
        {visibles.map(e => (
          <label key={e._id} style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44, padding: "4px 6px", borderBottom: `1px solid ${C.borde}`, cursor: "pointer" }}>
            <input type="checkbox" checked={elegidos.includes(e._id)} onChange={() => alternar(e._id)}
              style={{ width: 20, height: 20, accentColor: "var(--tab-azul)", flexShrink: 0 }} />
            <span style={{ fontSize: 14, overflowWrap: "anywhere" }}>
              {e.name}
              {e.rut ? <span style={{ color: C.suave, fontSize: 12 }}> · {e.rut}</span> : null}
            </span>
          </label>
        ))}
      </div>

      {/* (c) Resumen */}
      <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 10, background: C.boton, fontSize: 14 }}>
        {nSol === 0
          ? "Elija al menos un proveedor."
          : `Se generará${nSol === 1 ? " 1 solicitud" : `n ${nSol} solicitudes (una por proveedor)`} con ${nItems} ítem${nItems === 1 ? "" : "s"}${nSol > 1 ? " cada una, agrupadas para compararlas" : ""}.`}
        {faltaMaterial && <div style={{ color: C.ambar, fontSize: 13, marginTop: 4 }}>Falta indicar el material en algún ítem.</div>}
        {cantidadMala && <div style={{ color: C.rojo, fontSize: 13, marginTop: 4 }}>Las cantidades deben ser mayores que cero.</div>}
      </div>
      {error && <div style={{ color: C.rojo, fontSize: 13, marginTop: 8 }}>{error}</div>}

      {/* (d) Generar */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
        <button type="button" onClick={onCerrar} disabled={enviando} style={boton()}>Cancelar</button>
        <button type="button" onClick={generar} disabled={!puede}
          style={{ ...boton(C.verde, puede), fontWeight: 700, opacity: puede ? 1 : 0.5 }}>
          {enviando ? "Generando…" : nSol > 1 ? `Generar ${nSol} solicitudes` : "Generar solicitud"}
        </button>
      </div>
    </Hoja>
  );
};

export default CotizarAvisos;
